import { clientPollingManager } from './clientPolling.service';

/**
 * Serviço para gerenciar status e mapeamento de versões
 */
class VersoesStatusService {
  /**
   * Mapeia status da API para formato do componente
   */
  mapearStatusParaProgressData(status) {
    if (!status) return null;

    return {
      mensagem: status.mensagem || this._getMensagemPadrao(status),
      progresso: status.progresso || 0,
      sucesso: status.concluido ? true : undefined,
      erro: status.erro || false,
      empresaAtual: status.empresaAtual,
      motorAtual: status.motorAtual,
    };
  }

  /**
   * Retorna mensagem padrão baseada no status
   */
  _getMensagemPadrao(status) {
    if (status.aguardando || status.status === 'AGUARDANDO' || status.status === 'CRIADA_AGUARDANDO_EXECUCAO') {
      return 'Aguardando início...';
    }
    return 'Executando motores...';
  }

  /**
   * Verifica se uma versão está em processamento (aguardando, processando ou erro)
   */
  isVersaoAtiva(status) {
    if (!status) return false;

    // Se já está concluído, não está ativa
    if (status.concluido) return false;

    return (
      status.status === 'PROCESSANDO' ||
      status.status === 'ERRO' ||
      status.status === 'AGUARDANDO' ||
      status.status === 'CRIADA_AGUARDANDO_EXECUCAO' ||
      status.aguardando ||
      status.erro
    );
  }

  /**
   * Verifica se uma versão já foi concluída baseado nos dados da versão
   * Se a versão tem scripts disponíveis, significa que os motores já foram executados
   */
  isVersaoConcluida(versao) {
    if (!versao) return false;
    
    // Se tem scripts disponíveis, significa que os motores foram executados
    if (versao.scriptsDisponiveis && versao.scriptsDisponiveis.size > 0) {
      return true;
    }
    
    // Verificar também se há detalhes com motores concluídos
    const temMotoresConcluidos = versao?.versao?.detalhes?.some(
      (detalhe) => detalhe?.motores && detalhe.motores.length > 0
    );
    
    return temMotoresConcluidos || false;
  }

  /**
   * Busca status de uma versão
   */
  async buscarStatus(versaoId) {
    try {
      return await clientPollingManager.acompanharProgresso(versaoId);
    } catch (error) {
      console.error(`[VersoesStatusService] Erro ao buscar status da versão ${versaoId}:`, error);
      return null;
    }
  }

  /**
   * Verifica status de múltiplas versões
   */
  async verificarStatusVersoes(versoes) {
    if (!Array.isArray(versoes) || versoes.length === 0) {
      return [];
    }

    const agora = new Date();
    const horasLimite = 24; // Verificar apenas versões das últimas 24 horas

    // 1) Filtrar versões não concluídas e recentes
    const versoesParaVerificar = versoes.filter((versao) => {
      // Já concluída? então não precisa verificar no Redis
      if (this.isVersaoConcluida(versao)) {
        return false;
      }

      // Se tiver dtCadastro, aplicar filtro de \"recência\"
      const dtCriacao = versao.dtCadastro ? new Date(versao.dtCadastro) : null;
      if (dtCriacao instanceof Date && !Number.isNaN(dtCriacao.getTime())) {
        const horasDesdeCriacao = (agora - dtCriacao) / (1000 * 60 * 60);
        if (horasDesdeCriacao > horasLimite) {
          // Versão muito antiga: não faz sentido manter status no Redis
          return false;
        }
      }

      return true;
    });

    if (versoesParaVerificar.length === 0) {
      return [];
    }

    // 2) Buscar status em paralelo para as versões filtradas
    const promises = versoesParaVerificar.map(async (versao) => {
      try {
        const status = await this.buscarStatus(versao.idPreviaVersao);

        if (status && this.isVersaoAtiva(status)) {
          return {
            versaoId: versao.idPreviaVersao,
            status,
          };
        }
      } catch (error) {
        console.warn(
          `[VersoesStatusService] Erro ao buscar status da versão ${versao.idPreviaVersao}:`,
          error
        );
      }

      return null;
    });

    const resultados = await Promise.all(promises);
    return resultados.filter(Boolean);
  }

  /**
   * Cria status inicial para versão aguardando
   */
  criarStatusAguardando() {
    return {
      status: 'AGUARDANDO',
      aguardando: true,
      etapa: 'aguardando_inicio',
      progresso: 0,
      mensagem: 'Aguardando início da execução',
    };
  }
}

export const versoesStatusService = new VersoesStatusService();
export default VersoesStatusService;

